/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {translate} from '../locale/locale';
import {error} from '../logger/logger';
import {
  Assets,
  AssetType,
  BaseAsset,
  CVSAssetType,
  GenericObject
} from '../types/entities';
import {
  MapPosition
} from '../types/general';
import {
  AssetSearchableProperty,
  DeviceSearchTypes,
  SearchType
} from '../types/services';
import {
  isSame,
  Precision
} from '../utils/DateUtil';
import {
  safeJSONParse
} from '../utils/JSONUtils';
import {
  idMap
} from '../utils/ObjectUtil';
import {
  escapePostgreSQLString
} from '../utils/StringUtil';
import {
  getCVSAssets,
  getSCDevices,
  searchCVSDrivers,
  searchCVSFCDevices,
  searchGeoFences,
  searchMapSuggest
} from './ArcGIS';
import {getMatchedAssets} from './ArcGISUtils';
import {searchAssets, searchDevices, searchDrivers} from './Connect';
import {suggestMapSearch} from './Here';

export type GlobalSearchParams = {
  searchTerm: string;
  accountIds: number[];
  types: SearchType[];
  useCVS?: boolean;
  consolidate?: boolean;
  limitResults?: number;
  useHereApi?: boolean;
  showMatchLevel?: boolean;
  center?: MapPosition;
  includeSciDevice?: boolean;
  // matches geofence names whatever their case ...
  ignoreCase?: boolean;
  // overrides accountIds for the geofence search only ...
  geofenceAccountIds?: number[];
};

export type GlobalSearchResult = {
  name: string;
  type: SearchType;
  timestamp: number;
  item: any;
  isAsset?: boolean;
  isGeofence?: boolean;
  isDriver?: boolean;
  isPlace?: boolean;
  revalidated?: boolean;
  hasList?: boolean;
  isScDevice?: boolean;
  isFcDevice?: boolean;
};

export type GlobalSearchResults = {
  assets?: CVSAssetType[];
  drivers?: GenericObject[];
  geofences?: GenericObject[];
  places?: any;
  devices?: GenericObject[];
  fcdevices?: GenericObject[];
  consolidated?: GlobalSearchResult[];
};

export type RevalidationParams = {
  result: GlobalSearchResult;
  accountIds?: number[];
  // overrides accountIds for the geofence revalidation only ...
  geofenceAccountIds?: number[];
  useCVS?: boolean;
  useHereApi?: boolean;
  center?: [number, number];
  includeSciDevice?: boolean;
  isAssetQuery?: boolean;
};

export interface PlacesSearchParams {
  searchTerm: string;
  useHereApi?: boolean;
  showMatchLevel?: boolean;
  center?: MapPosition;
};

interface JoinAssetSearchResultsParams {
  cvsResults: BaseAsset[];
  assetsByLicense: Assets;
};

export enum GlobalSearchMapping {
  assets = 'assetsUI'
};

/**
 * The Global Search provides the underlying search facility for performing a platform-wide
 * freeform search for assets, vehicles, geofences, and drivers. When the useCVS argument
 * is true, Assets and Drivers will be searched against CVS Services, otherwise when false
 * (default), the search will be executed against CTC Platform Services.
 *
 * ```typescript
 *
 *    import {globalSearch} from '@ion/core/src/services';
 *    import {SearchType} from '@ion/core/src/types/services';
 *
 *    // perform a global search for assets (against ctc) and geofences for the given string ...
 *    const {assets, geofences} = await globalSearch({
 *      searchTerm : 'ion',
 *      types      : [
 *        SearchType.asset,
 *        SearchType.geofence
 *      ],
 *      accountIds
 *    ]});
 *
 *    ...
 *
 *    // perform a global search for assets (against cvs) and geofences for the given string ...
 *    const {assets, geofences} = await globalSearch({
 *      searchTerm : 'ion',
 *      types      : [
 *        SearchType.asset,
 *        SearchType.geofence
 *      ],
 *      useCVS: true,
 *      accountIds
 *    ]});
 *
 * ```
 */
export const globalSearch = async(params: GlobalSearchParams): Promise<GlobalSearchResults> => {
  const {
    types,
    useCVS,
    center,
    searchTerm,
    accountIds,
    useHereApi,
    consolidate,
    limitResults,
    showMatchLevel,
    includeSciDevice,
    ignoreCase,
    geofenceAccountIds
  } = params;
  const requests = [];
  if (types.includes(SearchType.asset)) {
    const api = useCVS ? cvsAssetSearch : ctcAssetSearch;
    if (useCVS) {
      requests.push(api({searchTerm, accountIds, limitResults, includeSciDevice}));
    } else {
      requests.push(api(searchTerm, accountIds, limitResults));
    }
  }
  if (types.includes(SearchType.driver)) {
    const api = useCVS ? cvsDriverSearch : ctcDriverSearch;
    requests.push(api(searchTerm, accountIds, limitResults));
  }
  if (types.includes(SearchType.geofence)) {
    requests.push(geofenceSearch(searchTerm, geofenceAccountIds ?? accountIds, {limitResults, ignoreCase}));
  }
  if (types.includes(SearchType.place)) {
    requests.push(placesSearch({
      showMatchLevel,
      searchTerm,
      useHereApi,
      center
    }));
  }
  if (types.includes(SearchType.fcdevice)) {
    const api = useCVS ? cvsFCDevicesSearch : ctcFCDevicesSearch;
    requests.push(api(searchTerm, accountIds, limitResults));
  }
  if (types.includes(SearchType.scdevice)) {
    requests.push(devicesSearch(searchTerm, accountIds, limitResults));
  }
  return Promise.all(requests).then((values) => {
    const results: GlobalSearchResults = {
      assets    : getMatchedAssets(values?.[0], searchTerm),
      drivers   : values?.[1],
      geofences : values?.[2],
      places    : values?.[3],
      fcdevices : values?.[4],
      devices   : values?.[5]
    };
    return consolidate ? normalize(results, useCVS, useHereApi) : results;
  });
};

/**
 * @ctcAssetSearch
 */
const ctcAssetSearch = async(
  searchTerm: string,
  accountIds: number | number[],
  searchProperty = AssetSearchableProperty.name
): Promise<any> => {
  try {
    const matches = await searchAssets({
      [AssetSearchableProperty.account] : accountIds.toString(),
      [searchProperty]                  : `*${searchTerm}*`
    });
    return matches;
  } catch (err) {
    return null;
  }
};

/**
 * @ctcDriverSearch
 */
const ctcDriverSearch = async(
  searchTerm: string,
  accountIds: number[]
): Promise<any> => {
  try {
    const matches = await searchDrivers({
      account   : accountIds[0],
      firstName : `*${searchTerm}*`
    });
    return matches;
  } catch (err) {
    return null;
  }
};

/**
 * Combines the results of a CVS search with asset information based on license number.
 * This function maps the asset information based on license numbers and
 * updates the CVS results with the matching asset information.
 */
const joinAssetSearchResults = ({
  cvsResults,
  assetsByLicense
}: JoinAssetSearchResultsParams) => {
  const mapped = idMap(assetsByLicense);
  return cvsResults?.map(result => {
    const id      = result.assetId;
    const matched = mapped?.[id];
    const getDisplayName = ({assetName}) => (
      translate('common:search.license', {
        licenseNumber : matched.licenseNumber,
        assetName
      })
    );
    return {
      ...result,
      ...(matched ? {
        isLicenseMatched : true,
        displayName      : getDisplayName(result)
      } : {})
    };
  });
};

type CvsAssetSearchProps = {
  searchTerm: string;
  accountIds: number[];
  limitResults?: number;
  includeSciDevice?: boolean;
};

/**
 * @cvsAssetSearch
 */
const cvsAssetSearch = async({
  searchTerm,
  accountIds,
  limitResults,
  includeSciDevice = false
}: CvsAssetSearchProps): Promise<any> => {
  try {
    const assetsByLicense = await ctcAssetSearch(searchTerm, accountIds, AssetSearchableProperty.licenseNumber);
    const assetsIds = assetsByLicense.map(({id}) => id);
    const matches   = await getCVSAssets({
      groups      : accountIds,
      searchByIds : assetsIds,
      searchtype  : 'asset',
      searchTerm,
      limitResults
    }, includeSciDevice);
    return joinAssetSearchResults({cvsResults: matches, assetsByLicense});
  } catch (err) {
    return null;
  }
};

const ctcFCDevicesSearch = async(
  searchTerm: string,
  accountIds: number[]
): Promise<any> => {
  try {
    const matches = await searchDevices(DeviceSearchTypes.DEVICE_MODEL, accountIds[0]);
    return matches;
  } catch (err) {
    return null;
  }
};

/**
 * @cvsDriverSearch
 */
const cvsDriverSearch = async(searchTerm: string, accountIds: number[], limitResults?: number): Promise<any> => {
  try {
    const matches = await searchCVSDrivers(searchTerm, accountIds, limitResults);
    return matches;
  } catch (err) {
    return null;
  }
};

/**
 * @geofenceSearch
 */
const geofenceSearch = async(
  searchTerm: string,
  accountIds: number[],
  {limitResults, ignoreCase}: {limitResults?: number; ignoreCase?: boolean} = {}
): Promise<any> => {
  try {
    const matches = await searchGeoFences({value: searchTerm, groupIds: accountIds, limitResults, ignoreCase});
    return matches;
  } catch (err) {
    return null;
  }
};

/**
 * @placesSearch
 */
const placesSearch = async({
  center,
  searchTerm,
  useHereApi = false,
  showMatchLevel = false
}: PlacesSearchParams): Promise<any> => {
  try {
    const api     = useHereApi ? suggestMapSearch : searchMapSuggest;
    const matches = await api({text: searchTerm, showMatchLevel, center});
    return matches;
  } catch (err) {
    return null;
  }
};

const cvsFCDevicesSearch = async(searchTerm: string, accountIds: number[], limitResults?: number): Promise<any> => {
  try {
    const matches = await searchCVSFCDevices(searchTerm, accountIds, limitResults);
    return matches;
  } catch (err) {
    return null;
  }
};

const devicesSearch = async(searchTerm: string, accountids: number[], limitResults: number): Promise<any> => {
  try {
    const resolved = escapePostgreSQLString(searchTerm);
    const params   = {
      groups    : accountids,
      assetid   : [],
      where     : {deviceLike: `device_esn ilike '%${resolved}%' or device_id_value ilike '%${resolved}%' or device_name ilike '%${resolved}%' or asset_name ilike '%${resolved}%'`},
      outFields : '*'
    };
    const results = await getSCDevices(params);
    return parseDeviceResults(results, searchTerm);
  } catch (err) {
    return null;
  }
};

/**
 * A private function just to breakdown the device result parse logic into new
 * function. This will add two new properties along with all device objects.
 * Those are type, and name. The type is always `AssetType.ScDevice` and the name
 * will dynamically set based on a matched property with search term
 */
const parseDeviceResults = (results: GenericObject, searchTerm: string) => (
  results?.data?.features?.map?.(({attributes}) => {
    const {
      device,
      device_name,
      device_esn,
      device_id_value
    } = attributes;
    const {id, esn}    = device || {};
    const defaultValue = device_name ??
      esn ??
      id ??
      device_esn ??
      device_id_value;
    const matched = [id, device_id_value, esn, device_esn, device_name].find(property => (
      property ? String(property).toLowerCase().includes(searchTerm.toLowerCase()) : false
    )) || defaultValue;
    return {...attributes, type: AssetType.ScDevice, name: matched};
  })
);

/**
 * @revalidate
 *
 * Given a GlobalSearchResult, determines if the result is stale and, if so,
 * revalidates the search result, retrieving and returning the latest update
 * for the results.
 */
export const revalidate = async(params: RevalidationParams, expiry?: Precision) => {
  const {result} = params;
  if (result) {
    if (!isSame(result.timestamp, Date.now(), (expiry || expiries[result.type])) || !result?.item) {
      try {
        const updated = await revalidators[result.type]?.(params, params.useHereApi, params.center);
        return updated;
      } catch (err) {
        error(err);
        return result;
      }
    }
  }
  return result;
};

/**
 * Defines the default stale revalidation expiries for each global search type.
 */
const expiries = {
  [SearchType.asset]    : 'second',
  [SearchType.driver]   : 'second',
  [SearchType.fcdevice] : 'second',
  [SearchType.geofence] : 'day',
  [SearchType.place]    : 'day'
};
/**
 * Defines the default Search field for CVS query
 */
const searchField = {
  [SearchType.asset]    : 'assetName',
  [SearchType.driver]   : 'operatorNames',
  [SearchType.geofence] : 'landmarkName',
  [SearchType.fcdevice] : 'deviceEsn'
};

const getProperty = (searchType, useCVS) => {
  if (useCVS) {
    return searchField[searchType];
  }
  return null;
};

/**
 * Defines normalizeSearch to used to remove extra space
 */
const normalizeSearch = (search: string): string => {
  return search?.replaceAll(',  ', ', ').split('(')[0].trim();
};

/**
 * @private
 *
 * Defines the revalidation implementations for each search type. Revalidation can
 * be employed by calling the revalidate method,
 */
const revalidators = {
  [SearchType.asset] : async(params: RevalidationParams) => {
    if (params.useCVS) {
      params.isAssetQuery = true;
    }
    return await revalidator(
      params.useCVS ? cvsAssetSearch : ctcAssetSearch,
      params,
      getProperty(SearchType.asset, params.useCVS)
    );
  },
  [SearchType.driver] : async(params: RevalidationParams) => {
    return await revalidator(
      params.useCVS ? cvsDriverSearch : ctcDriverSearch,
      params,
      getProperty(SearchType.driver, params.useCVS)
    );
  },
  [SearchType.geofence] : async(params: RevalidationParams) => {
    return await revalidator(
      geofenceSearch,
      {...params, accountIds: params.geofenceAccountIds ?? params.accountIds},
      getProperty(SearchType.geofence, params.useCVS)
    );
  },
  [SearchType.fcdevice] : async(params: RevalidationParams) => {
    return await revalidator(
      geofenceSearch,
      params,
      getProperty(SearchType.fcdevice, params.useCVS)
    );
  },
  [SearchType.place] : async({result, useHereApi, center}: RevalidationParams) => {
    const search   = normalizeSearch(result.name);
    const response = await placesSearch({searchTerm: search, useHereApi, center});
    const updated  = response.data?.suggestions.find(
      ({label, text}) => label === search || text === search
    );
    return {...result, item: updated, revalidated: true};
  }
};

/**
 * @private
 */
const revalidator = async(
  api: Function,
  {result, accountIds, isAssetQuery, includeSciDevice}: RevalidationParams,
  property: string
) => {
  const {name} = result || {};
  if (!isEmpty(name)) {
    const results = await (isAssetQuery && includeSciDevice
      ? api({searchTerm: name, accountIds, includeSciDevice})
      : api(name, accountIds));
    const nameNormalized = name?.replace(/, /g, ',');
    const item           = property ? results?.find(item =>
      item[property] === name || item[property] === nameNormalized) : null;
    return {
      ...result,
      item        : item || (results?.[0] || result.item),
      timestamp   : Date.now(),
      revalidated : true
    };
  }
  return {};
};

/**
 * @normalize
 *
 * Given GlobalSearchResults, flattens all results down to a single collection
 * and transforms each result to a GlobalSearchResult within the collection.
 */
export const normalize = (results: GlobalSearchResults, useCVS = false, useHereApi = false): GlobalSearchResults => {
  const {assets, drivers, fcdevices, geofences, places, devices} = results;
  let consolidated = [];
  if (assets?.length) {
    consolidated = consolidated.concat(
      transformSearch(assets, SearchType.asset, useCVS ? ['assetName', 'assetAlias', 'assets'] : ['name', 'externalId', 'assets'])
    );
  }
  if (devices) {
    consolidated = consolidated.concat(transformSearch(devices, SearchType.scdevice, ['name']));
  }
  if (geofences?.length) {
    consolidated = consolidated.concat(
      transformSearch(geofences, SearchType.geofence, ['landmarkName'])
    );
  }
  if (drivers?.length) {
    consolidated = consolidated.concat(
      transformSearch(drivers, SearchType.driver, useCVS ? ['operatorNames'] : ['lastName', 'firstName'])
    );
  }
  if (fcdevices?.length) {
    consolidated = consolidated.concat(
      transformSearch(fcdevices, SearchType.fcdevice, useCVS ? ['deviceEsn'] : ['esn'])
    );
  }
  if (places) {
    const {suggestions} = places?.data;
    if (suggestions) {
      consolidated = consolidated.concat(
        transformSearch(suggestions, SearchType.place, [useHereApi ? 'label' : 'text'])
      );
    }
  }
  return {...results, consolidated};
};

const formatFromJSONString = (val) => {
  const result = safeJSONParse(val);
  if (result) {
    return result?.map(({name}) => name).join(', ') || '';
  }
};

const getFieldValues = (item, property: string) => {
  const value = item?.[property];
  if (property === 'assets' && value) {
    const parsed = formatFromJSONString(value);
    if (parsed) {
      item[GlobalSearchMapping.assets] = parsed;
      return item[GlobalSearchMapping.assets];
    }
  }
  return item[property];
};

/**
 * @transformSearch
 *
 * Transforms all discrete search results into a single collection of results
 * which conform to a normalized data structure.
 */
export const transformSearch = (items: any[], type: SearchType, fields = ['name']): GlobalSearchResult[] => {
  const timestamp = Date.now();
  return items?.map(item => {
    const name = fields.map(f => getFieldValues(item, f)).filter(v => !isNil(v)).toString().replace(',', ', ');
    return {
      isAsset     : type === SearchType.asset,
      isGeofence  : type === SearchType.geofence,
      isDriver    : type === SearchType.driver,
      isPlace     : type === SearchType.place,
      isVehicle   : type === SearchType.vehicle,
      isFcDevice  : type === SearchType.fcdevice,
      hasList     : (type === SearchType.asset && item.assets !== null),
      isSciDevice : (type === SearchType.scdevice && item.features !== null),
      timestamp,
      type,
      name,
      item
    };
  });
};

export default globalSearch;
